<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯' : '新增' }}短網址</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="handleCancel" :disabled="loading">取消</button>
        <button class="btn btn-primary" @click="handleSubmit" :disabled="loading || !isFormValid">
          {{ loading ? '處理中...' : (isEdit ? '更新' : '建立') }}
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="form-container">
      <div class="form-section">
        <h3>基本資訊</h3>
        <div class="form-grid">
          <div class="form-group">
            <label>設定名稱 <span class="required">*</span></label>
            <input 
              type="text" 
              class="form-control" 
              v-model="form.configName" 
              placeholder="例如：活動頁面短網址"
              :disabled="loading"
            />
          </div>
          
          <div class="form-group">
            <label>描述</label>
            <textarea 
              class="form-control" 
              v-model="form.description" 
              rows="3" 
              placeholder="選填：說明此短網址的用途"
              :disabled="loading"
            ></textarea>
          </div>

          <div class="form-group">
            <label>目標網址 <span class="required">*</span></label>
            <input 
              type="url" 
              class="form-control"
              :class="{ 'input-error': urlTouched && !isUrlValid }"
              v-model="form.targetUrl"
              placeholder="https://example.com/page"
              :disabled="loading"
              @blur="urlTouched = true"
            />
            <p v-if="urlTouched && !isUrlValid && form.targetUrl" class="form-error">
              <i class="ri-error-warning-line"></i>
              網址格式不正確，請輸入完整網址（需包含 https:// 或 http://）
            </p>
            <p v-else class="form-hint">
              <i class="ri-information-line"></i>
              完整的目標網址，例如：<code>https://example.com/campaign/summer-2024</code>
            </p>
          </div>
        </div>
      </div>

      <div class="form-section">
        <h3>LINE 頻道設定</h3>
        <p class="section-description">
          選擇此短網址對應的 LINE 官方帳號頻道。設定後，點擊短網址會先進入 LIFF 頁面確認加好友，再跳轉到目標網址。
        </p>
        <div class="form-grid">
          <div class="form-group">
            <label>LINE 頻道 <span class="required">*</span></label>
            <select
              class="form-control"
              v-model="form.lineConfigId"
              :disabled="loading || lineConfigsLoading"
            >
              <option value="">請選擇 LINE 頻道</option>
              <option
                v-for="config in lineConfigs"
                :key="config.id"
                :value="config.id"
              >
                {{ config.configName || config.messagingApi?.botBasicId || config.id }}
                {{ config.messagingApi?.botBasicId ? `(${config.messagingApi.botBasicId})` : '' }}
              </option>
            </select>
            <p class="form-hint">
              <i class="ri-information-line"></i>
              選擇後，點擊短網址的用戶會透過 LIFF 加好友後再跳轉目標網址，並記錄 LINE User ID。
            </p>
          </div>
        </div>
      </div>

      <div class="form-section">
        <h3>自動貼標設定</h3>
        <p class="section-description">
          當會員點擊此短網址時，系統會自動為該會員貼上指定的標籤。
        </p>
        <div class="form-grid">
          <div class="form-group">
            <label>自動標籤</label>
            <TagSelectorMultiple
              v-model="form.autoTags"
              :disabled="loading"
            />
            <p class="form-hint">
              <i class="ri-information-line"></i>
              選填：選擇當會員點擊此短網址時要自動貼上的標籤。可選擇多個標籤。
            </p>
          </div>
        </div>
      </div>

      <div class="form-section">
        <h3>其他設定</h3>
        <div class="form-grid">
          <div class="form-group">
            <label>短網址過期時間</label>
            <input 
              type="datetime-local" 
              class="form-control" 
              v-model="form.expiresAt"
              :disabled="loading"
            />
            <p class="form-hint">
              <i class="ri-information-line"></i>
              <strong>短網址層級控制：</strong>
              <span v-if="form.expiresAt">
                此短網址將在 <strong>{{ formatExpiresAt(form.expiresAt) }}</strong> 後失效，無法再被點擊使用。
              </span>
              <span v-else>
                此短網址將永久有效，除非手動停用。
              </span>
              <br>
              <span style="color: #999; font-size: 12px;">
                💡 注意：此設定與標籤的時間屬性不同。標籤的時間屬性控制標籤在會員身上的有效期，而此設定控制短網址本身的有效期。
              </span>
            </p>
          </div>

          <div class="form-group" v-if="isEdit">
            <label class="checkbox-label">
              <input type="checkbox" v-model="form.isActive" :disabled="loading" />
              啟用此短網址
            </label>
          </div>
        </div>
      </div>

    </div>
    </div>
  </div>
</template>

<script>
import lineShortUrlApi from '@/api/modules/lineShortUrl.js';
import lineConfigApi from '@/api/modules/lineConfig.js';
import TagSelectorMultiple from '@/components/TagSelectorMultiple.vue';
import { showError } from '@/utils/swal.js';

export default {
  name: 'LineShortUrlCreateEdit',
  components: {
    TagSelectorMultiple
  },
  data() {
    return {
      loading: false,
      lineConfigsLoading: false,
      isEdit: false,
      lineConfigs: [],
      urlTouched: false,
      form: {
        configName: '',
        description: '',
        targetUrl: '',
        lineConfigId: '',
        autoTags: [],
        expiresAt: '',
        isActive: true
      }
    };
  },
  computed: {
    isUrlValid() {
      if (!this.form.targetUrl) return false;
      try {
        const url = new URL(this.form.targetUrl);
        return url.protocol === 'https:' || url.protocol === 'http:';
      } catch {
        return false;
      }
    },
    isFormValid() {
      return this.form.configName && this.form.targetUrl && this.isUrlValid;
    }
  },
  mounted() {
    this.isEdit = !!this.$route.params.id;
    this.loadLineConfigs();
    if (this.isEdit) {
      this.loadShortUrl();
    }
  },
  methods: {
    async loadLineConfigs() {
      this.lineConfigsLoading = true;
      try {
        const response = await lineConfigApi.list({ pageSize: 100 });
        if (response && response.success) {
          this.lineConfigs = response.data?.items || response.data || [];
          // 只有一個頻道時自動選取（新增模式才自動選）
          if (!this.isEdit && this.lineConfigs.length === 1) {
            this.form.lineConfigId = this.lineConfigs[0].id;
          }
        }
      } catch (error) {
        console.error('載入 LINE 頻道失敗:', error);
      } finally {
        this.lineConfigsLoading = false;
      }
    },

    async loadShortUrl() {
      this.loading = true;
      try {
        const response = await lineShortUrlApi.get(this.$route.params.id);
        if (response && response.success && response.data) {
          const data = response.data;
          
          this.form = {
            configName: data.urlName || data.configName || '',
            description: data.description || '',
            targetUrl: data.targetUrl || '',
            lineConfigId: data.lineConfigId || '',
            autoTags: data.autoTags || [],
            expiresAt: data.expiresAt ? this.formatDateTimeLocal(data.expiresAt) : '',
            isActive: data.isActive !== false
          };
        }
      } catch (error) {
        console.error('載入短網址失敗:', error);
        await showError('載入失敗', '請稍後再試');
        this.handleCancel();
      } finally {
        this.loading = false;
      }
    },

    async handleSubmit() {
      this.urlTouched = true;
      if (!this.form.configName) {
        await showError('請填寫必填欄位', '設定名稱為必填');
        return;
      }
      if (!this.form.targetUrl) {
        await showError('請填寫必填欄位', '目標網址為必填');
        return;
      }
      if (!this.isUrlValid) {
        await showError('網址格式不正確', '請輸入完整網址，需包含 https:// 或 http://');
        return;
      }

      this.loading = true;
      try {
        const submitData = {
          configName: this.form.configName,
          description: this.form.description || null,
          targetUrl: this.form.targetUrl,
          lineConfigId: this.form.lineConfigId || null,
          autoTags: this.form.autoTags || [],
        };

        // 處理過期時間（必須明確轉換為 ISO 格式或 null）
        if (this.form.expiresAt && this.form.expiresAt.trim()) {
          submitData.expiresAt = this.convertToISOString(this.form.expiresAt);
        } else {
          submitData.expiresAt = null;
        }

        // 編輯時才加入 isActive
        if (this.isEdit) {
          submitData.isActive = this.form.isActive;
        }

        let response;
        if (this.isEdit) {
          response = await lineShortUrlApi.update(this.$route.params.id, submitData);
        } else {
          response = await lineShortUrlApi.create(submitData);
        }
        
        if (response && response.success) {
          this.$router.push({ name: 'ClientLineShortUrlList' });
        }
      } catch (error) {
        console.error('提交失敗:', error);
        await showError(this.isEdit ? '更新失敗' : '建立失敗', error?.response?.data?.message || '請稍後再試');
      } finally {
        this.loading = false;
      }
    },

    handleCancel() {
      this.$router.push({ name: 'ClientLineShortUrlList' });
    },

    formatDateTimeLocal(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    },

    convertToISOString(dateTimeLocal) {
      if (!dateTimeLocal) return null;
      return new Date(dateTimeLocal).toISOString();
    },

    formatExpiresAt(dateTimeLocal) {
      if (!dateTimeLocal) return '';
      const date = new Date(dateTimeLocal);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left { display: flex; align-items: center; }
.header-actions { display: flex; align-items: center; gap: 8px; }

.page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.page-container {
  padding: 20px;
  padding-top: 72px;
  max-width: 900px;
}

.form-container {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-section {
  margin-bottom: 32px;
}

.form-section h3 {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: #333;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 8px;
}

.section-description {
  color: #666;
  font-size: 14px;
  margin: 0 0 16px 0;
  line-height: 1.6;
}

.form-grid {
  display: grid;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
}

.required {
  color: #ff4d4f;
}

.form-control {
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
}

.form-control.input-error {
  border-color: #ef4444;
  background: #fff5f5;
}

.form-control.input-error:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.form-control:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.form-error {
  font-size: 13px;
  color: #ef4444;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  line-height: 1.5;
}

.form-hint {
  font-size: 13px;
  color: #666;
  margin-top: 6px;
  line-height: 1.5;
}

.form-hint i {
  margin-right: 4px;
}

.form-hint code {
  background: #f0f0f0;
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 12px;
  font-family: 'Courier New', monospace;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.btn {
  padding: 6px 16px;
  border-radius: 6px;
}
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
.btn-secondary:hover:not(:disabled) { background: #e5e7eb; }
.btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }

@media (max-width: 768px) {
  .page-container {
    padding: 12px;
    padding-top: 72px;
  }
  .form-container {
    padding: 16px;
  }
}
</style>
